import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Request interceptor:
//  (a) Wenn der Request ein FormData-Body hat (Datei-Upload), müssen wir den
//      Content-Type axios selbst setzen lassen (Boundary wird sonst kaputt).
//  (b) **Bearer-Fallback**: secure=true; SameSite=none-Cookies werden von
//      iOS Safari, Brave, Firefox-Strict und Chrome mit „3rd-Party-Cookies
//      blockieren" oft NICHT gesetzt → Cookie-only-Auth scheitert auf
//      Production. Wir hängen den access_token aus localStorage zusätzlich
//      als `Authorization: Bearer …` Header an. Das Backend
//      (auth_utils._extract_token) prüft bereits beide Quellen.
api.interceptors.request.use((config) => {
  if (config.data instanceof FormData && config.headers) {
    if (typeof config.headers.delete === "function") {
      config.headers.delete("Content-Type");
    } else {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  // Bearer-Token als Fallback, falls cross-site cookies blockiert sind
  try {
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null;
    if (token && config.headers) {
      // Existierende Authorization-Header nicht überschreiben (z. B. /auth/refresh)
      const existing = (typeof config.headers.get === "function")
        ? config.headers.get("Authorization")
        : (config.headers["Authorization"] || config.headers["authorization"]);
      if (!existing) {
        if (typeof config.headers.set === "function") {
          config.headers.set("Authorization", `Bearer ${token}`);
        } else {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
      }
    }
  } catch (_e) {
    // localStorage kann in privaten Modi werfen — ignorieren
  }
  return config;
});

// Attach a response interceptor for 401 → try refresh once
let refreshPromise = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    if (error.response?.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post("/auth/refresh").finally(() => {
            refreshPromise = null;
          });
        }
        const refreshRes = await refreshPromise;
        // Wenn der Server einen neuen access_token im Body zurückgibt
        // (Fallback für Clients ohne Cookie-Support), in localStorage updaten,
        // damit der Request-Interceptor ihn beim Retry mitsendet.
        const newToken = refreshRes?.data?.access_token;
        if (newToken && typeof localStorage !== "undefined") {
          try { localStorage.setItem("access_token", newToken); } catch (_e) { /* ignore */ }
        }
        return api(original);
      } catch (e) {
        // fall through
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  // null/undefined → leerer String → Caller fällt auf e.message zurück
  // (statt einen generischen "Ein Fehler ist aufgetreten" zu liefern, der
  // alle echten Diagnostik-Infos verschluckt — wichtig z.B. bei axios
  // Timeouts / Network-Errors / CORS-Issues).
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  const s = String(detail);
  // Nie "[object Object]" an den User durchreichen
  return s.startsWith("[object") ? "" : s;
}

export function apiError(e, fallback = "Unerwarteter Fehler") {
  // Reihenfolge: server-detail → message (z.B. "timeout of 60000ms exceeded"
  // oder "Network Error") → fallback. Auf dem Weg konvertieren wir die
  // technischen axios-Messages in benutzerfreundliche deutsche Texte.
  const detail = formatApiErrorDetail(e?.response?.data?.detail);
  if (detail) return detail;
  const m = e?.message || "";
  if (m.toLowerCase().includes("network error")) {
    return "Keine Verbindung zum Server — bitte Netzwerk prüfen und erneut versuchen.";
  }
  if (m.toLowerCase().includes("timeout")) {
    return "Server hat nicht rechtzeitig geantwortet — bitte erneut versuchen.";
  }
  return m || fallback;
}
